import React, { useState } from 'react';
import ModalBase from './ModalBase';
import PackCopySpecs from './PackCopySpecs';
import { SpecDetail } from '../types';
import { postPackCopyManual } from '../utils/backendServices';
import { useUploadContext } from '../contexts/UploadContext';
import useURLParams from '../hooks/useURLParams';
import '../styles/components/UploadPackCopy.scss';
import { useNavigateWithinProject } from '../utils/navigation';

interface CreatePackCopyManualModalProps {
    onClose: () => void;
    onBack: () => void;
    onFinish: (specs: SpecDetail[]) => void;
    packCopyData: {
        versionName: string;
        notes: string;
    };
    projectId: string;
}

const CreatePackCopyManualModal: React.FC<CreatePackCopyManualModalProps> = ({
    onClose,
    onBack,
    onFinish,
    packCopyData,
    projectId,
}) => {
    const [manualSpecs, setManualSpecs] = useState<SpecDetail[]>([]);
    const [isCreating, setIsCreating] = useState(false);

    const { triggerFetch } = useUploadContext();
    const { setParam } = useURLParams();

    const navigate = useNavigateWithinProject();

    const handleSpecsChange = (specs: SpecDetail[]) => {
        setManualSpecs(specs);
    };

    const handleSpecDelete = (displayName: string) => {
        // Remove all specs with the given display name
        const updatedSpecs = manualSpecs.filter(
            (spec) => spec.display_name !== displayName,
        );
        setManualSpecs(updatedSpecs);
    };

    const handleFinish = async () => {
        if (manualSpecs.length === 0) return;

        setIsCreating(true);

        try {
            const packCopyId = await postPackCopyManual(
                {
                    version_name: packCopyData.versionName,
                    notes: packCopyData.notes || undefined,
                    project_id: projectId,
                },
                manualSpecs,
            );

            triggerFetch();
            setParam('modal', null);
            onFinish(manualSpecs);
            onClose();
            navigate(`/pack-copies/${packCopyId}`);
        } catch (error) {
            console.error('Error creating manual pack copy:', error);
            // TODO: Show error message to user
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <ModalBase onClose={onClose} width="1000px">
            <div className="upload-components">
                <h2 className="modal-title">
                    Enter Specs for {packCopyData.versionName}
                </h2>
                <p className="modal-description">
                    Add individual specs, table specs, and more.
                </p>

                <PackCopySpecs
                    specs={manualSpecs}
                    onSpecCreation={handleSpecsChange}
                    onSpecDelete={handleSpecDelete}
                    autoOpenAddModal={manualSpecs.length === 0}
                    useContext={false}
                />

                <div className="button-row">
                    <button
                        className="btn-secondary"
                        onClick={onBack}
                        disabled={isCreating}
                    >
                        Back
                    </button>
                    <button
                        className="btn-primary"
                        onClick={handleFinish}
                        disabled={manualSpecs.length === 0 || isCreating}
                    >
                        {isCreating
                            ? 'Creating...'
                            : `Create Pack Copy (${manualSpecs.length} spec${manualSpecs.length !== 1 ? 's' : ''})`}
                    </button>
                </div>
            </div>
        </ModalBase>
    );
};

export default CreatePackCopyManualModal;
